/**
 * Голос персонажа — выбор из каталога голосов ElevenLabs («Все голоса» на странице персонажа, pages/assets).
 *
 * По образцу Artlist (Voices → See all): список голосов с пробой, подпись «мужской · взрослый · русский», фильтры
 * пол / возраст / язык / акцент, поиск по имени. Обложка и «волна» — рисунок из id голоса, у каждого голоса свой и
 * одинаковый при каждом открытии. Нажатие на обложку — проба голоса, на строку — выбор: голос сразу сохраняет
 * хозяин пикера (onPick), отдельной кнопки «Сохранить» нет. Пробы играет общий useVoicePreview страницы «Озвучка»:
 * одна проба за раз, уход со страницы её останавливает.
 *
 * Каталог по умолчанию — русские голоса: родные русские из библиотеки и стандартные, проверенные ElevenLabs на
 * русском. «Все языки» снимает этот фильтр. Сервер принимает только голос, который сам показал в каталоге.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'

import { parseAssetError } from '../../api/assets'
import {
	DEFAULT_VOICE_FILTERS,
	getVoiceCatalog,
	type VoiceAccent,
	type VoiceAge,
	type VoiceCard,
	type VoiceFilters,
	type VoiceGender,
} from '../../api/voiceCatalog'
import { useVoiceProbe } from './useVoiceProbe'
import { voiceCover, voiceMetaLine, voiceWave } from '../../utils/voiceCard'

const GENDERS: { key: VoiceGender | null; label: string }[] = [
	{ key: null, label: 'Любой' },
	{ key: 'm', label: 'Мужской' },
	{ key: 'f', label: 'Женский' },
]
const AGES: { key: VoiceAge | null; label: string }[] = [
	{ key: null, label: 'Любой возраст' },
	{ key: 'young', label: 'Молодой' },
	{ key: 'adult', label: 'Взрослый' },
	{ key: 'old', label: 'Пожилой' },
]
const ACCENTS: { key: VoiceAccent | null; label: string }[] = [
	{ key: null, label: 'Любой акцент' },
	{ key: 'american', label: 'Американский' },
	{ key: 'british', label: 'Британский' },
	{ key: 'australian', label: 'Австралийский' },
	{ key: 'indian', label: 'Индийский' },
	{ key: 'african', label: 'Африканский' },
]
const SEARCH_DELAY_MS = 350


export function PlayGlyph({ playing }: { playing: boolean }) {
	return playing ? (
		<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
			<rect x="6" y="5" width="4" height="14" rx="1.2" fill="currentColor" />
			<rect x="14" y="5" width="4" height="14" rx="1.2" fill="currentColor" />
		</svg>
	) : (
		// Треугольник смещён вправо внутри viewBox: оптический центр, а не геометрический.
		<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
			<path d="M8.5 5.2v13.6a1 1 0 0 0 1.52.85l10.6-6.8a1 1 0 0 0 0-1.7l-10.6-6.8a1 1 0 0 0-1.52.85z" fill="currentColor" />
		</svg>
	)
}

/** Ждём файл пробы — вращающееся кольцо. */
export function BusyGlyph() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true" className="char-voice__busy">
			<path d="M12 3a9 9 0 1 1-6.36 2.64" />
		</svg>
	)
}

function CheckGlyph() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M5 12.5l4.5 4.5L19 7.5" />
		</svg>
	)
}

export function VoiceRow({
	voice,
	selected,
	playing,
	busy = false,
	saving,
	disabled,
	onPlay,
	onPick,
}: {
	voice: VoiceCard
	selected: boolean
	playing: boolean
	/** Файл пробы фразой ещё запрашивается у сервера. */
	busy?: boolean
	saving: boolean
	disabled: boolean
	onPlay: () => void
	onPick: () => void
}) {
	// 16 столбиков: в узкой карточке персонажа подпись голоса важнее рисунка.
	const wave = useMemo(() => voiceWave(voice.id, 16), [voice.id])
	const meta = voiceMetaLine(voice)
	return (
		<div className={`char-voice__row${selected ? ' is-selected' : ''}${playing ? ' is-playing' : ''}${busy ? ' is-busy' : ''}`}>
			<button
				type="button"
				className="char-voice__cover"
				style={{ background: voiceCover(voice.id) }}
				onClick={onPlay}
				disabled={!voice.preview_url || busy}
				aria-busy={busy}
				aria-label={playing ? `Остановить пробу: ${voice.name}` : `Прослушать: ${voice.name}`}
				title={voice.preview_url ? (busy ? 'Готовим пробу…' : undefined) : 'У этого голоса нет пробы'}
			>
				{voice.preview_url && (busy ? <BusyGlyph /> : <PlayGlyph playing={playing} />)}
			</button>
			<button
				type="button"
				role="radio"
				aria-checked={selected}
				className="char-voice__pick"
				onClick={onPick}
				disabled={disabled}
				aria-busy={saving}
			>
				<span className="char-voice__text">
					<span className="char-voice__name">{voice.name}</span>
					{meta && <span className="char-voice__meta">{meta}</span>}
				</span>
				<span className="char-voice__wave" aria-hidden="true">
					{wave.map((h, i) => (
						<i key={i} style={{ '--h': h, '--i': i } as CSSProperties} />
					))}
				</span>
				<span className="char-voice__state" aria-hidden="true">
					{saving ? <span className="char-voice__spin" /> : selected ? <CheckGlyph /> : null}
				</span>
			</button>
		</div>
	)
}

export default function CharacterVoicePicker({
	current,
	onPick,
	initial,
	allowClear = true,
	characterId = null,
}: {
	/** Голос персонажа сейчас; null — не выбран. */
	current: VoiceCard | null
	/** Чей персонаж: пробы играют его фразой (своя фраза важнее общей). */
	characterId?: string | null
	/** Сохранить выбор (null — убрать голос); ошибка — текстом под списком. */
	onPick: (voice: VoiceCard | null) => Promise<void>
	/** Стартовые фильтры (у Artlist «See all» открывается с полом и возрастом текущего голоса). */
	initial?: Partial<VoiceFilters>
	allowClear?: boolean
}) {
	const [filters, setFilters] = useState<VoiceFilters>({ ...DEFAULT_VOICE_FILTERS, ...initial })
	const [searchText, setSearchText] = useState('')
	const [voices, setVoices] = useState<VoiceCard[]>([])
	const [page, setPage] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [note, setNote] = useState<string | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	// id голоса, который сохраняется, или 'clear' — снятие голоса.
	const [saving, setSaving] = useState<string | null>(null)
	const [saveError, setSaveError] = useState<string | null>(null)
	const seq = useRef(0)
	// probing — чья проба фразой ещё запрашивается (loading занят списком голосов).
	const { playing, loading: probing, play, stop } = useVoiceProbe()

	// Закрыли карточку посреди пробы — проба замолкает.
	useEffect(() => () => stop(), [stop])

	// Поиск — после паузы в наборе: каждая буква не должна уходить запросом.
	useEffect(() => {
		const t = window.setTimeout(() => {
			setFilters((f) => (f.search === searchText ? f : { ...f, search: searchText }))
		}, SEARCH_DELAY_MS)
		return () => window.clearTimeout(t)
	}, [searchText])

	const load = useCallback(async (f: VoiceFilters, p: number) => {
		// Ответ на устаревшие фильтры не должен перезаписать свежий список.
		const my = ++seq.current
		setLoading(true)
		if (p === 0) setLoadError(null)
		try {
			const res = await getVoiceCatalog(f, p)
			if (my !== seq.current) return
			setVoices((prev) => {
				const base = p === 0 ? [] : prev
				const seen = new Set(base.map((v) => v.id))
				return [...base, ...res.voices.filter((v) => !seen.has(v.id))]
			})
			setHasMore(res.has_more)
			setPage(p)
			setNote(res.error)
		} catch (e) {
			if (my !== seq.current) return
			setLoadError(parseAssetError(e))
			if (p === 0) {
				setVoices([])
				setHasMore(false)
			}
		} finally {
			if (my === seq.current) setLoading(false)
		}
	}, [])

	useEffect(() => {
		void load(filters, 0)
	}, [filters, load])

	const pick = async (voice: VoiceCard | null) => {
		if (saving) return
		if (voice && current?.id === voice.id) return
		setSaving(voice ? voice.id : 'clear')
		setSaveError(null)
		try {
			await onPick(voice)
		} catch (e) {
			setSaveError(parseAssetError(e))
		} finally {
			setSaving(null)
		}
	}

	const setFilter = (patch: Partial<VoiceFilters>) => setFilters((f) => ({ ...f, ...patch }))

	return (
		<div className="char-voice">
			{current && (
				<div className="char-voice__current">
					<VoiceRow
						voice={current}
						selected
						playing={playing === current.id}
						saving={false}
						disabled
						busy={probing === current.id}
						onPlay={() => void play(current, current.id, characterId)}
						onPick={() => {}}
					/>
					{allowClear && (
						<button
							type="button"
							className="char-voice__clear"
							onClick={() => void pick(null)}
							disabled={saving !== null}
						>
							{saving === 'clear' ? 'Убираем…' : 'Убрать голос'}
						</button>
					)}
				</div>
			)}

			<div className="char-voice__filters">
				<div className="char-voice__seg" role="group" aria-label="Пол голоса">
					{GENDERS.map((g) => (
						<button
							key={g.label}
							type="button"
							className={filters.gender === g.key ? 'is-on' : undefined}
							aria-pressed={filters.gender === g.key}
							onClick={() => setFilter({ gender: g.key })}
						>
							{g.label}
						</button>
					))}
				</div>
				<div className="char-voice__seg" role="group" aria-label="Возраст голоса">
					{AGES.map((a) => (
						<button
							key={a.label}
							type="button"
							className={filters.age === a.key ? 'is-on' : undefined}
							aria-pressed={filters.age === a.key}
							onClick={() => setFilter({ age: a.key })}
						>
							{a.label}
						</button>
					))}
				</div>
				<div className="char-voice__seg" role="group" aria-label="Язык голоса">
					<button
						type="button"
						className={filters.language === 'ru' ? 'is-on' : undefined}
						aria-pressed={filters.language === 'ru'}
						onClick={() => setFilter({ language: 'ru' })}
					>
						Русские
					</button>
					<button
						type="button"
						className={filters.language === 'all' ? 'is-on' : undefined}
						aria-pressed={filters.language === 'all'}
						onClick={() => setFilter({ language: 'all' })}
					>
						Все языки
					</button>
				</div>
				{/* Акцент — у голосов на других языках (у русских он один); выбирает человек, по внешности не угадываем. */}
				{filters.language === 'all' && (
					<div className="char-voice__seg" role="group" aria-label="Акцент голоса">
						{ACCENTS.map((a) => (
							<button
								key={a.label}
								type="button"
								className={(filters.accent ?? null) === a.key ? 'is-on' : undefined}
								aria-pressed={(filters.accent ?? null) === a.key}
								onClick={() => setFilter({ accent: a.key })}
							>
								{a.label}
							</button>
						))}
					</div>
				)}
				<input
					className="char-voice__search"
					type="search"
					value={searchText}
					maxLength={60}
					placeholder="Поиск по имени"
					aria-label="Поиск голоса по имени"
					onChange={(e) => setSearchText(e.target.value)}
				/>
			</div>

			{note && <div className="char-voice__note" role="status">{note}</div>}
			{loadError && (
				<div className="char-voice__note char-voice__note--error" role="alert">
					<span>{loadError}</span>
					<button type="button" onClick={() => void load(filters, 0)}>Повторить</button>
				</div>
			)}

			<div className="char-voice__list" role="radiogroup" aria-label="Голоса" aria-busy={loading}>
				{voices.map((v) => (
					<VoiceRow
						key={v.id}
						voice={v}
						selected={current?.id === v.id}
						playing={playing === v.id}
						saving={saving === v.id}
						disabled={saving !== null}
						busy={probing === v.id}
						onPlay={() => void play(v, v.id, characterId)}
						onPick={() => void pick(v)}
					/>
				))}
				{loading && voices.length === 0 &&
					[0, 1, 2].map((i) => <div key={i} className="char-voice__row char-voice__row--skeleton" aria-hidden="true" />)}
			</div>

			{!loading && !loadError && voices.length === 0 && (
				<div className="char-voice__empty">Под эти фильтры голосов нет — снимите фильтр или поищите по-другому.</div>
			)}
			{hasMore && (
				<button
					type="button"
					className="char-voice__more"
					disabled={loading}
					onClick={() => void load(filters, page + 1)}
				>
					{loading ? 'Загружаем…' : 'Показать ещё'}
				</button>
			)}
			{saveError && <em className="char-voice__err" role="alert">{saveError}</em>}
		</div>
	)
}
